import { normalizeEffectPlaybackAudience } from "../../models/contracts";

export type EffectRendererKind = "screen" | "crowd";
export type CrowdPlaybackMode = "offset" | "synchronized";
export const CROWD_EFFECT_MEDIA_FIT = "contain" as const;

export interface CrowdEffectLayout {
  count: number;
  gap: number;
  playerHeight: number;
  playerWidth: number;
}

const clamp = (value: number, minimum: number, maximum: number): number =>
  Math.min(maximum, Math.max(minimum, value));

export const resolveEffectRendererKind = (playbackAudience: unknown): EffectRendererKind =>
  normalizeEffectPlaybackAudience(playbackAudience) === "Crowd" ? "crowd" : "screen";

export function calculateCrowdEffectLayout(viewportWidth: number, viewportHeight: number): CrowdEffectLayout {
  const width = Math.max(1, viewportWidth);
  const height = Math.max(1, viewportHeight);
  const horizontalPadding = clamp(width * 0.04, 16, 64) * 2;
  const availableWidth = Math.max(1, width - horizontalPadding);
  const gap = clamp(width * 0.012, 12, 28);
  const targetHeight = height * 0.9;
  const targetWidth = targetHeight * 9 / 16;
  const count = clamp(Math.floor((availableWidth + gap) / (targetWidth + gap)), 1, 5);
  const widthLimitedPlayerWidth = (availableWidth - gap * (count - 1)) / count;
  const playerHeight = Math.min(targetHeight, widthLimitedPlayerWidth * 16 / 9);

  return {
    count,
    gap,
    playerHeight,
    playerWidth: playerHeight * 9 / 16,
  };
}

export function crowdEffectOffsets(durationSeconds: number, count: number, mode: CrowdPlaybackMode): number[] {
  const safeCount = clamp(Math.floor(count), 1, 5);
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0 || mode === "synchronized") {
    return Array.from({ length: safeCount }, () => 0);
  }
  return Array.from({ length: safeCount }, (_, index) => durationSeconds * index / safeCount);
}

export const crowdEffectPlayerKeys = (eventId: string, count: number): string[] =>
  Array.from({ length: clamp(Math.floor(count), 1, 5) }, (_, index) => `${eventId}:${index}`);

export function crowdEffectPosition(
  serverNowMs: number,
  startedAtMs: number,
  durationSeconds: number,
  offsetSeconds: number,
): number {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return 0;
  const elapsedSeconds = Math.max(0, serverNowMs - startedAtMs) / 1_000;
  return ((elapsedSeconds + offsetSeconds) % durationSeconds + durationSeconds) % durationSeconds;
}

export function circularPlaybackDrift(actual: number, expected: number, durationSeconds: number): number {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return Math.abs(actual - expected);
  const direct = Math.abs(actual - expected);
  return Math.min(direct, durationSeconds - direct);
}
